export type HttpErrorResponse = { status?: number; data?: unknown } | null | undefined;

const SESSION_REVOKED = 'SESSION_REVOKED';
const AUTH_FAILURE_CODE = 401;
const FORBIDDEN_STATUS = 403;
// Contrato de frontCourses.getOne en i2c_v2: el 403 de licencia no trae errorKey, así que este texto
// literal es lo único que lo distingue de los 403 de permiso (no ser miembro del curso o del colegio).
const LICENSE_DENIED_MESSAGE = "Unauthorized, you don't have active license for this course";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * i2c_v2 serializes JWT failures as HTTP 403 with `code: 401` (and `data.error: 401`) in the body;
 * a revoked session also carries `errorKey: 'SESSION_REVOKED'`. The controllers' `responseError`
 * shape nests it in `error.errorKey`; `error.code` is not checked there because the license 403
 * also sends `error.code: 403`.
 */
function isAuthFailureBody(body: unknown): boolean {
  if (!isRecord(body)) return false;

  const data = isRecord(body.data) ? body.data : undefined;
  const error = isRecord(body.error) ? body.error : undefined;

  return (
    body.code === AUTH_FAILURE_CODE ||
    data?.error === AUTH_FAILURE_CODE ||
    body.errorKey === SESSION_REVOKED ||
    data?.errorKey === SESSION_REVOKED ||
    error?.errorKey === SESSION_REVOKED
  );
}

function isForbiddenResponse(response: HttpErrorResponse): boolean {
  return response?.status === FORBIDDEN_STATUS;
}

export function isAuthFailureResponse(response: HttpErrorResponse): boolean {
  return isForbiddenResponse(response) && isAuthFailureBody(response?.data);
}

/**
 * A 403 rejected before reaching the backend (edge WAF): its body is HTML, plain text or empty,
 * never the JSON object the backend returns for its own auth, permission or license errors.
 */
export function isEdgeBlockedResponse(response: HttpErrorResponse): boolean {
  return isForbiddenResponse(response) && !isRecord(response?.data);
}

export function isLicenseDeniedResponse(response: HttpErrorResponse): boolean {
  if (!isForbiddenResponse(response) || !isRecord(response?.data)) return false;

  const error = isRecord(response.data.error) ? response.data.error : undefined;

  return error?.message === LICENSE_DENIED_MESSAGE;
}
